// JavaScript
// screens/ForgotPasswordScreen.js
import React, { useState } from 'react';
import {View, Text, TextInput, Pressable, ActivityIndicator, Alert} from 'react-native';

import Auth from '../../auth/AuthHelper';

export default function ForgotPasswordScreen({ route, navigation }) {
  const initialUsername = route?.params?.username || '';
  const [username, setUsername] = useState(initialUsername);
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [step, setStep] = useState('enter-username'); // 'enter-username' | 'code-sent' | 'done'
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const setError = (err) => setMessage(err?.message || String(err) || 'Something went wrong');

  const handleSendCode = async () => {
    const user = (username || '').trim();


    if (!user) {
      setMessage('Please enter your email/username.');
      return;
    }
    setLoading(true);
    setMessage('');
    try {
      const result = await Auth.requestPasswordReset({ username: user });
      const delivery =
        result?.nextStep?.codeDeliveryDetails || result?.codeDeliveryDetails || null;
      const hint = delivery?.deliveryMedium && delivery?.destination
        ? `${delivery.deliveryMedium} to ${delivery.destination}`
        : null;

      setStep('code-sent');
      setMessage(hint ? `Code sent: ${hint}` : 'If the account exists, a code has been sent.');
    } catch (e) {
      // Intentionally non-specific to avoid leaking account existence
      setMessage('If the account exists, a code has been sent.');
    } finally {
      setLoading(false);
    }
  };

  // const handleConfirm = async () => {
  //   const user = (username || '').trim();
  //   const codeVal = (code || '').trim();
  //   const pwd = newPassword || '';
  //   if (!user || !codeVal || !pwd) {
  //     setMessage('Please fill all fields.');
  //     return;
  //   }
  //   setLoading(true);
  //   setMessage('');
  //   try {
  //     await Auth.confirmPasswordReset(
  //     {username: user},
  //         {confirmationCode: codeVal},
  //         {newPassword: pwd},
  //     );
  //     setStep('done');
  //     setMessage('Password updated. You can now sign in.');
  //     setTimeout(() => navigation.goBack(), 600);
  //   } catch (e) {
  //     setError(e);
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const handleConfirm = async () => {
    const user = (username || '').trim();
    const codeVal = (code || '').trim();
    const pwd = newPassword || '';
    if (!user || !codeVal || !pwd) {
      setMessage('Please fill all fields.');
      return;
    }
    setLoading(true);
    setMessage('');
    try {
      await Auth.confirmPasswordReset(
        { username: user },
        { confirmationCode: codeVal },
        { newPassword: pwd },
      );
      setStep('done');
      setMessage('Password updated. You can now sign in.');
      Alert.alert(
        'Password updated',
        'You can now sign in.',
        [
          {
            text: 'OK',
            onPress: () => navigation.goBack(),
          },
        ],
        { cancelable: false }
      );
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  };


  const handleResend = async () => {
    if (!username.trim()) {
      setMessage('Enter your email/username to resend the code.');
      return;
    }
    await handleSendCode();
  };

  return (
    <View style={{ flex: 1, padding: 20, backgroundColor: '#fff', justifyContent: 'center' }}>
      <Text style={{ fontSize: 22, fontWeight: '700', marginBottom: 16 }}>Reset your password</Text>

      {/* Step 1: Ask for username/email */}
      {step === 'enter-username' && (
        <>
          <Text style={{ marginBottom: 8, color: '#374151' }}>Email or username</Text>
          <TextInput
            value={username}
            onChangeText={setUsername}
            placeholder="you@example.com"
            autoCapitalize="none"
            autoCorrect={false}
            keyboardType="email-address"
            placeholderTextColor="#9CA3AF"
            style={{
              borderWidth: 1, borderColor: '#E5E7EB', borderRadius: 8, padding: 12, marginBottom: 16,
            }}
            selectionColor="#0EA5E9"
            textContentType="username"
          />

          <Pressable
            onPress={handleSendCode}
            disabled={loading}
            style={({ pressed }) => [
              {
                backgroundColor: '#0EA5E9',
                paddingVertical: 12,
                borderRadius: 8,
                alignItems: 'center',
                opacity: loading || pressed ? 0.8 : 1,
              },
            ]}
          >
            {loading ? (
              <ActivityIndicator color="#fff" />
            ) : (
              <Text style={{ color: '#fff', fontWeight: '700' }}>Send reset code</Text>
            )}
          </Pressable>
        </>
      )}

      {/* Step 2: Enter code + new password */}
      {step === 'code-sent' && (
        <>
          <Text style={{ marginBottom: 8, color: '#374151' }}>Verification code</Text>
          <TextInput
            value={code}
            onChangeText={setCode}
            placeholder="123456"
            autoCapitalize="none"
            autoCorrect={false}
            keyboardType="number-pad"
            placeholderTextColor="#9CA3AF"
            style={{
              borderWidth: 1, borderColor: '#E5E7EB', borderRadius: 8, padding: 12, marginBottom: 12,
            }}
            selectionColor="#0EA5E9"
            textContentType="oneTimeCode"
          />

          <Text style={{ marginBottom: 8, color: '#374151' }}>New password</Text>
          <TextInput
            value={newPassword}
            onChangeText={setNewPassword}
            placeholder="Enter a new password"
            secureTextEntry
            autoCapitalize="none"
            autoCorrect={false}
            placeholderTextColor="#9CA3AF"
            style={{
              borderWidth: 1, borderColor: '#E5E7EB', borderRadius: 8, padding: 12, marginBottom: 16,
            }}
            selectionColor="#0EA5E9"
            textContentType="newPassword"
          />

          <Pressable
            onPress={handleConfirm}
            disabled={loading}
            style={({ pressed }) => [
              {
                backgroundColor: '#10B981',
                paddingVertical: 12,
                borderRadius: 8,
                alignItems: 'center',
                opacity: loading || pressed ? 0.8 : 1,
              },
            ]}
          >
            {loading ? (
              <ActivityIndicator color="#fff" />
            ) : (
              <Text style={{ color: '#fff', fontWeight: '700' }}>Reset password</Text>
            )}
          </Pressable>

          <Pressable
            onPress={handleResend}
            disabled={loading}
            style={({ pressed }) => [{ paddingVertical: 10, alignItems: 'center' }, pressed && { opacity: 0.7 }]}
          >
            <Text style={{ color: '#1e88e5', fontWeight: '600' }}>Resend code</Text>
          </Pressable>
        </>
      )}

      {message ? (
        <Text style={{ marginTop: 16, color: '#374151' }}>{message}</Text>
      ) : null}

      <Pressable
        onPress={() => navigation.goBack()}
        style={({ pressed }) => [{ paddingVertical: 14, alignItems: 'center' }, pressed && { opacity: 0.7 }]}
      >
        <Text style={{ color: '#1e88e5', fontWeight: '600' }}>Back to Sign in</Text>
      </Pressable>
    </View>
  );
}
